// The page side of the audio bridge: creates the AudioContext and the voice's
// AudioWorkletNode, starts the voice, sends controls and assets, reads the
// telemetry batches, returns their buffers to the pool, keeps load statistics and
// recovers the voice after an exception. See docs/contracts/audio-bridge.md.
//
// Two processors speak the protocol: the wasm host (worklet.ts, VOICE_PROCESSOR),
// which runs the psim-wasm voice, and the mock (mock-processor.ts, MOCK_PROCESSOR).
// Nothing here depends on which one runs. loadVoiceWasm() and loadEngineBundle()
// fetch what the voice's init carries.

import voiceWasmUrl from "../generated/wasm/psim.wasm?url";
import mockProcessorUrl from "../generated/worklets/mock-processor.js?url";
import voiceProcessorUrl from "../generated/worklets/worklet.js?url";
import {
  PROTOCOL_VERSION,
  R_ENTRY_MS,
  SAMPLE_RATE,
  TelemetryBatch,
  readRecord,
  type AssetKind,
  type AssetMessage,
  type BridgeProcessorOptions,
  type ErrorMessage,
  type FromProcessor,
  type HelloMessage,
  type InitMessage,
  type Powertrain,
  type ReadyMessage,
  type StatsMessage,
  type TelemetryRecord,
  type TimelineEvent,
  type ToProcessor,
} from "./protocol";

/** A processor the engine can load: its worklet module URL and registered name. */
export interface ProcessorModule {
  url: string;
  name: string;
}

export const MOCK_PROCESSOR: ProcessorModule = { url: mockProcessorUrl, name: "psim-mock" };
export const VOICE_PROCESSOR: ProcessorModule = { url: voiceProcessorUrl, name: "psim-voice" };

let wasmBytes: Promise<ArrayBuffer> | null = null;

/** The voice's WebAssembly bytes (crates/psim-wasm, built by scripts/prebuild/10-build-wasm.mjs), fetched once. */
export function loadVoiceWasm(): Promise<ArrayBuffer> {
  wasmBytes ??= fetch(voiceWasmUrl).then((r) => {
    if (!r.ok) throw new Error(`${voiceWasmUrl}: HTTP ${r.status}`);
    return r.arrayBuffer();
  });
  return wasmBytes;
}

// One bundle per data/engines/<id>/, written by scripts/prebuild/20-bundle-engines.mjs, loaded on demand.
const engineBundles = import.meta.glob<string>("../generated/engines/*.json", { query: "?raw", import: "default" });

/** The ids of the engines with a bundle, sorted. */
export function engineIds(): string[] {
  return Object.keys(engineBundles)
    .map((path) => path.replace(/^.*\/(.*)\.json$/, "$1"))
    .sort();
}

/** The engine bundle of data/engines/<id>/ as UTF-8 bytes, for init's bundle. */
export async function loadEngineBundle(id: string): Promise<Uint8Array> {
  const load = engineBundles[`../generated/engines/${id}.json`];
  if (load === undefined) throw new Error(`no engine bundle ${id}; there are ${engineIds().join(", ")}`);
  return new TextEncoder().encode(await load());
}

export type MessageDirection = "to-processor" | "from-processor";

export interface EngineOptions extends BridgeProcessorOptions {
  /** An existing context (for example an OfflineAudioContext); by default a 48 kHz AudioContext. */
  context?: BaseAudioContext;
  /** The processor to run; the mock by default. */
  processor?: ProcessorModule;
  /** Output channels of the node (default 2, the same signal on each). */
  channels?: number;
  /** Connect the node to the context's destination (default true). */
  connect?: boolean;
  /** How many times the engine re-sends init after an exception in process() (default 3). */
  recoverLimit?: number;
  /**
   * Sees every port message in both directions, before it is sent or handled. It
   * must read a message at once: a telemetry buffer is transferred right after.
   */
  tap?: (direction: MessageDirection, message: ToProcessor | FromProcessor) => void;
}

/** What the page starts the voice with; the engine turns it into init and asset messages. */
export interface StartJob {
  /** The engine bundle, as UTF-8 bytes or as text the engine encodes. */
  bundle: Uint8Array | string;
  powertrain: Powertrain;
  timeline?: TimelineEvent[];
  assets?: { id: string; kind: AssetKind; bytes: ArrayBuffer }[];
  /** The voice's WebAssembly bytes; the voice processor needs them (loadVoiceWasm()). */
  wasm?: ArrayBuffer;
  /** A render job file (TOML), as text or UTF-8 bytes, for the voice to render exactly. */
  job?: Uint8Array | string;
}

/** Load and deadline statistics over the last full window of about a second of audio. */
export interface LoadStats {
  /** Share of the audio time spent in process(), 0 to 1 (Date.now steps, so ±0.1 %). */
  load: number;
  /** Gaps between process() calls longer than the stall threshold, since the start. */
  stalls: number;
  /** The longest gap between two process() calls in the window, in ms. */
  maxGapMs: number;
  /** Wall-clock time minus audio time over the window, in ms; grows when the audio falls behind. */
  driftMs: number;
  /** Records the processor dropped because its pool was empty, since the start. */
  dropped: number;
  /** Batches that never arrived, from gaps in the sequence numbers. */
  lostBatches: number;
  /** AudioContext.playbackStats underruns where the browser reports them (Chrome 146+), else null. */
  underruns: number | null;
  /** Batches received since the start. */
  batches: number;
}

type Listener<T> = (value: T) => void;

/** Window over which load and drift are averaged. */
const WINDOW_SECONDS = 1;

export class AudioEngine {
  readonly context: BaseAudioContext;
  readonly node: AudioWorkletNode;
  /** Resolves with the processor's hello. */
  readonly hello: Promise<HelloMessage>;

  private readonly options: EngineOptions;
  private readonly telemetryListeners = new Set<Listener<TelemetryBatch>>();
  private readonly errorListeners = new Set<Listener<ErrorMessage>>();
  private readonly readyListeners = new Set<Listener<ReadyMessage>>();
  private readonly statsWaiters: Listener<StatsMessage>[] = [];
  private lastStart: { init: InitMessage; assets: AssetMessage[] } | null = null;
  private recoveries = 0;
  private newest: TelemetryRecord | null = null;
  private readyMessage: ReadyMessage | null = null;

  private batches = 0;
  private nextSeq = 0;
  private lost = 0;
  private dropped = 0;
  private stalls = 0;
  private windowStart: { frames: number; processMs: number; entryMs: number } | null = null;
  private windowMaxGap = 0;
  private window: { load: number; maxGapMs: number; driftMs: number } = { load: 0, maxGapMs: 0, driftMs: 0 };

  private constructor(context: BaseAudioContext, node: AudioWorkletNode, options: EngineOptions) {
    this.context = context;
    this.node = node;
    this.options = options;
    let helloResolve: Listener<HelloMessage> = () => {};
    this.hello = new Promise((resolve) => (helloResolve = resolve));
    node.port.onmessage = (e: MessageEvent<FromProcessor>) => this.onMessage(e.data, helloResolve);
    node.onprocessorerror = () =>
      this.emitError({
        type: "error",
        where: "processor",
        message: "processorerror: the processor threw outside its guard and is stopped",
        frame: -1,
        fatal: true,
      });
  }

  /** Creates the context (unless given), loads the processor module and makes its node. */
  static async create(options: EngineOptions = {}): Promise<AudioEngine> {
    const context = options.context ?? new AudioContext({ sampleRate: SAMPLE_RATE, latencyHint: "interactive" });
    const processor = options.processor ?? MOCK_PROCESSOR;
    await context.audioWorklet.addModule(processor.url);
    const channels = options.channels ?? 2;
    const processorOptions: BridgeProcessorOptions = {};
    if (options.poolSize !== undefined) processorOptions.poolSize = options.poolSize;
    if (options.batchHz !== undefined) processorOptions.batchHz = options.batchHz;
    if (options.stallMs !== undefined) processorOptions.stallMs = options.stallMs;
    const node = new AudioWorkletNode(context, processor.name, {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [channels],
      processorOptions,
    });
    if (options.connect !== false) node.connect(context.destination);
    const engine = new AudioEngine(context, node, options);
    await engine.hello;
    return engine;
  }

  /** Starts (or restarts) the voice. Resolves with its ready message. */
  start(job: StartJob): Promise<ReadyMessage> {
    const bundle = typeof job.bundle === "string" ? new TextEncoder().encode(job.bundle) : job.bundle;
    const init: InitMessage = { type: "init", protocol: PROTOCOL_VERSION, bundle, powertrain: job.powertrain };
    if (job.timeline !== undefined) init.timeline = job.timeline;
    if (job.wasm !== undefined) init.wasm = job.wasm;
    if (job.job !== undefined) init.job = typeof job.job === "string" ? new TextEncoder().encode(job.job) : job.job;
    const assets: AssetMessage[] = (job.assets ?? []).map((a) => ({ type: "asset", ...a }));
    if (assets.length > 0) init.assets = assets.map((a) => ({ id: a.id, kind: a.kind }));
    this.lastStart = { init, assets };
    this.recoveries = 0;
    const ready = this.nextReady();
    this.sendStart();
    // An AudioParam set before init still counts: the processor reads it as the starting value.
    const rpm = job.powertrain.load.params["rpm"];
    const param = this.node.parameters.get("rpm");
    if (rpm !== undefined && param !== undefined) param.setValueAtTime(rpm, this.context.currentTime);
    return ready;
  }

  /** Resolves with the next ready message, or rejects on the next fatal error. */
  nextReady(): Promise<ReadyMessage> {
    return new Promise((resolve, reject) => {
      const onReady = (m: ReadyMessage): void => {
        this.readyListeners.delete(onReady);
        this.errorListeners.delete(onError);
        resolve(m);
      };
      const onError = (m: ErrorMessage): void => {
        if (!m.fatal || m.where === "process") return; // a process error is followed by recovery
        this.readyListeners.delete(onReady);
        this.errorListeners.delete(onError);
        reject(new Error(`${m.where}: ${m.message}`));
      };
      this.readyListeners.add(onReady);
      this.errorListeners.add(onError);
    });
  }

  /**
   * Changes a control. One the processor declares as an AudioParam (continuous
   * controls such as rpm) is set through the AudioParam at the current time; any
   * other goes as a port message and applies at the next block.
   */
  control(id: string, value: number): void {
    const param = this.node.parameters.get(id);
    if (param !== undefined) param.setValueAtTime(value, this.context.currentTime);
    else this.post({ type: "control", id, value });
  }

  /** Sends an asset. The bytes are copied, so the page keeps them for a restart. */
  sendAsset(id: string, kind: AssetKind, bytes: ArrayBuffer): void {
    const asset: AssetMessage = { type: "asset", id, kind, bytes };
    this.post(asset);
    if (this.lastStart !== null) {
      this.lastStart.assets = this.lastStart.assets.filter((a) => a.id !== id).concat(asset);
    }
  }

  /** Asks the processor for its telemetry pool's state. */
  requestStats(): Promise<StatsMessage> {
    return new Promise((resolve) => {
      this.statsWaiters.push(resolve);
      this.post({ type: "stats" });
    });
  }

  /** Calls `listener` with each batch. The batch is valid only during the call. */
  onTelemetry(listener: Listener<TelemetryBatch>): () => void {
    this.telemetryListeners.add(listener);
    return () => this.telemetryListeners.delete(listener);
  }

  onError(listener: Listener<ErrorMessage>): () => void {
    this.errorListeners.add(listener);
    return () => this.errorListeners.delete(listener);
  }

  onReady(listener: Listener<ReadyMessage>): () => void {
    this.readyListeners.add(listener);
    return () => this.readyListeners.delete(listener);
  }

  /** The newest telemetry record, or null before the first batch. */
  get latest(): TelemetryRecord | null {
    return this.newest;
  }

  /** The init message the voice was last started with, or null before start(); the pose instance is built from it (#33). */
  get init(): InitMessage | null {
    return this.lastStart?.init ?? null;
  }

  /** The assets the voice was last started with, bytes and all (#41): a pose instance of the same voice needs its IRs. */
  get startAssets(): readonly AssetMessage[] {
    return this.lastStart?.assets ?? [];
  }

  /** The ready message of the running voice, or null. */
  get ready(): ReadyMessage | null {
    return this.readyMessage;
  }

  stats(): LoadStats {
    const playback = (this.context as { playbackStats?: { underrunEvents?: number } }).playbackStats;
    return {
      load: this.window.load,
      stalls: this.stalls,
      maxGapMs: this.window.maxGapMs,
      driftMs: this.window.driftMs,
      dropped: this.dropped,
      lostBatches: this.lost,
      underruns: typeof playback?.underrunEvents === "number" ? playback.underrunEvents : null,
      batches: this.batches,
    };
  }

  /** Starts the audio after a user gesture (browsers block audio before one). */
  async resume(): Promise<void> {
    if (this.context instanceof AudioContext && this.context.state !== "running") await this.context.resume();
  }

  async close(): Promise<void> {
    this.node.port.onmessage = null;
    this.node.disconnect();
    if (this.context instanceof AudioContext && this.options.context === undefined) await this.context.close();
  }

  private post(message: ToProcessor, transfer: Transferable[] = []): void {
    this.options.tap?.("to-processor", message);
    this.node.port.postMessage(message, transfer);
  }

  private sendStart(): void {
    if (this.lastStart === null) return;
    this.readyMessage = null;
    this.post(this.lastStart.init);
    for (const asset of this.lastStart.assets) this.post(asset);
  }

  private onMessage(msg: FromProcessor, helloResolve: Listener<HelloMessage>): void {
    this.options.tap?.("from-processor", msg);
    if (msg instanceof ArrayBuffer) {
      this.onBatch(msg);
      return;
    }
    switch (msg.type) {
      case "hello":
        if (msg.protocol !== PROTOCOL_VERSION) {
          this.emitError({
            type: "error",
            where: "protocol",
            message: `processor speaks protocol ${msg.protocol}, the page ${PROTOCOL_VERSION}`,
            frame: -1,
            fatal: true,
          });
        }
        helloResolve(msg);
        break;
      case "ready":
        this.readyMessage = msg;
        for (const l of [...this.readyListeners]) l(msg);
        break;
      case "error":
        this.emitError(msg);
        if (msg.where === "process" && this.recoveries < (this.options.recoverLimit ?? 3)) {
          this.recoveries++;
          this.sendStart(); // the voice's state is undefined after a throw: start it again
        }
        break;
      case "stats":
        this.statsWaiters.shift()?.(msg);
        break;
    }
  }

  private emitError(msg: ErrorMessage): void {
    for (const l of [...this.errorListeners]) l(msg);
  }

  private onBatch(buffer: ArrayBuffer): void {
    const batch = new TelemetryBatch(buffer);
    this.batches++;
    if (batch.seq > this.nextSeq) this.lost += batch.seq - this.nextSeq;
    this.nextSeq = batch.seq + 1;
    this.dropped = batch.droppedTotal;
    this.stalls = batch.stallsTotal;
    if (batch.count > 0) {
      this.updateWindow(batch);
      this.newest = readRecord(batch, batch.count - 1);
    }
    try {
      for (const l of this.telemetryListeners) l(batch);
    } finally {
      // Home to the pool, whatever a listener did.
      this.post(buffer, [buffer]);
    }
  }

  private updateWindow(batch: TelemetryBatch): void {
    const last = batch.count - 1;
    const now = {
      frames: batch.framesTotal,
      processMs: batch.processMsTotal,
      entryMs: batch.get(last, R_ENTRY_MS),
    };
    this.windowMaxGap = Math.max(this.windowMaxGap, batch.maxGapMs);
    if (this.windowStart === null) {
      this.windowStart = now;
      return;
    }
    const audioMs = ((now.frames - this.windowStart.frames) / batch.sampleRate) * 1000;
    if (audioMs < WINDOW_SECONDS * 1000) return;
    this.window = {
      load: (now.processMs - this.windowStart.processMs) / audioMs,
      maxGapMs: this.windowMaxGap,
      driftMs: now.entryMs - this.windowStart.entryMs - audioMs,
    };
    this.windowStart = now;
    this.windowMaxGap = 0;
  }
}
